<template>

  <v-sheet width="100%"  class="ma-2">
    <div id="heatmap-metap-container" style="width: 95%; min-height: 500px; margin: 0 auto;"></div>
  </v-sheet>
</template>

<script>
import Highcharts from "highcharts";
import HighchartsHeatmap from "highcharts/modules/heatmap";
import HighchartsExporting from "highcharts/modules/exporting";

HighchartsHeatmap(Highcharts);
HighchartsExporting(Highcharts);

export default {
  name: "PhenotypeHallmarkMetap",
  props: {
    tdata: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      heatmapData: [],
      categoriesX: [],
      categoriesY: ["Site", "Protein", "RNA"],
      xAxisLabelColors: {},
      chart: null
    };
  },
  methods: {
    getColor(value) {
      const absValue = Math.abs(value);
      if (absValue > 0.05) return "#FAFAFA";
      if (absValue <= 0.05 && absValue > 0.01) return value > 0 ? "#FFA07A" : "#ADD8E6";
      if (absValue <= 0.01) return value > 0 ? "#B71C1C" : "#1E88E5";
      return "#FAFAFA";
    },

    processData(rawData) {
      this.categoriesX = [...new Set(rawData.map((d) => d.phenotype.replace("HALLMARK_", "")))]
          .sort((a, b) => a.localeCompare(b));  // A-Z 排序

      this.xAxisLabelColors = {};
      const isnocolor = (c) => ["#FAFAFA"].includes(c);
      const isLight = (c) => ["#FFA07A", "#ADD8E6"].includes(c);
      const isDark = (c) => ["#B71C1C", "#1E88E5"].includes(c);
      const isSameColor = (c1, c2) => c1 === c2;

      this.heatmapData = rawData.flatMap((item) => {
        const phenotype = item.phenotype.replace("HALLMARK_", "");

        const xIndex = this.categoriesX.indexOf(phenotype);

        if (!(phenotype in this.xAxisLabelColors)) {
          const siteVal = item.ptm_metap;
          const rnaVal = item.rna_metap;
          const proteinVal = item.protein_metap;

          const siteColor = this.getColor(siteVal);
          const rnaColor = this.getColor(rnaVal);
          const proteinColor = this.getColor(proteinVal);

          const shouldColor =
              isDark(siteColor) &&
              (
                  isnocolor(proteinColor) ||
                  isLight(proteinColor) ||
                  (siteVal * proteinVal < 0)
              ) &&
              !isSameColor(rnaColor, siteColor);

          this.xAxisLabelColors[phenotype] = shouldColor ? siteColor : "#333333";
        }

        const createPoint = (yIndex, value) => {
          const color = this.getColor(value);
          let group = "Non-significant";
          const absValue = Math.abs(value);
          if (absValue <= 0.05 && absValue > 0.01) {
            group = value > 0 ? "0.01 < p < 0.05, positive" : "0.01 < p < 0.05, negative";
          } else if (absValue <= 0.01) {
            group = value > 0 ? "p < 0.01, positive" : "p < 0.01, negative";
          }
          return { x: xIndex, y: yIndex, value, color, group };
        };

        return [
          createPoint(0, item.ptm_metap),
          createPoint(1, item.protein_metap),
          createPoint(2, item.rna_metap),
        ];
      });
    },

    renderHeatmap() {
      if (this.chart) {
        // this.chart.destroy();
        this.chart = null;
      }

      Highcharts.chart("heatmap-metap-container", {
        chart: {
          type: "heatmap",
          marginBottom:360,
          plotBorderWidth: 0.2,
          spacingTop: 10,
          spacingBottom: 20,
          reflow: true,

        },
        title: {
          text: "Comparing Phenotype Correlation at Site, RNA, and Protein Levels",
          align: "center"
        },
        xAxis: {
          categories: this.categoriesX,
          title: { text: "Phenotype", style: { fontSize: "1rem", color: "black" } },
          labels: {
            rotation: -45,
            style: {
              fontSize: "11px",
            },
            overflow: "justify",
            crop: false,
            align: "right",
            useHTML: false,
            formatter: function () {
              const label = this.value;
              const labelColors = this.chart.options.customLabelColors || {};
              const color = labelColors[label] || "#333333";

              const isCustom = label in labelColors && color !== "#333333";

              const style = `
                          color: ${color};
                          ${isCustom ? "font-weight: bold;" : ""}
                        `;
              return `<span style="${style}">${label}</span>`;
            },
            step: 1
          }
        },
        yAxis: {
          categories: this.categoriesY,
          title: { text: "Omics", style: { fontSize: "1rem", color: "black" } }
        },
        colorAxis: {
          dataClasses: [
            { from: 0.01, to: 1, color: "#B71C1C", name: "p < 0.01, positive" },
            { from: 0.01, to: 0.05, color: "#FFA07A", name: "0.01 < p < 0.05, positive" },
            { from: -0.05, to: 0.05, color: "#FAFAFA", name: "non-significant" },
            { from: -0.05, to: -0.01, color: "#ADD8E6", name: "0.01 < p < 0.05, negative" },
            { from: -1, to: -0.01, color: "#1E88E5", name: "p < 0.01, negative" }
          ]
        },
        legend: {
          align: "right",
          layout: "vertical",
          verticalAlign: "middle",
          x: -10,
          y: -170,
          symbolHeight: 20,
          symbolWidth: 40,
          symbolRadius: 0,
          title: {
            text: "Pancancer meta p-values",
            style: {
              fontSize: "12px",
              fontWeight: "bold",
              color: "#333333"
            }
          },
          itemStyle: {
            fontSize: "12px",
            color: "black",
          }
        },
        tooltip: {
          formatter() {
            return `<b>Omics:</b> ${this.series.yAxis.categories[this.point.y]}<br>
                    <b>Phenotype:</b> ${this.series.xAxis.categories[this.point.x]}<br>
                    <b>Value:</b> ${this.point.value}`;
          }
        },
        series: [{
          name: "Heatmap Data",
          borderWidth: 0.5,
          borderColor: "#fffff9",
          data: this.heatmapData,
          dataLabels: { enabled: false }
        }],
        exporting: {
          enabled: true,
          scale: 4,
          chartOptions: {
            chart: {
              width: document.getElementById("heatmap-metap-container").offsetWidth,
              height: document.getElementById("heatmap-metap-container").offsetHeight
            }
          },
          buttons: {
            contextButton: {
              menuItems: ["downloadPNG", "downloadSVG"]
            }
          }
        },
        customLabelColors: this.xAxisLabelColors
      });
    },

    processplot() {
      if (this.tdata.length > 0) {
        this.processData(this.tdata);
        this.$nextTick(() => this.renderHeatmap());
      }
    }
  },
  mounted() {
    this.processplot();
  },
  beforeDestroy() {
      this.chart = null;

  }

};
</script>

<style scoped>
#heatmap-metap-container {
  margin: 0 auto;
}
</style>


